import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { useElectionContext } from '@/shared/election/context'

/**
 * Vote Tallying Centre — the module shell.
 *
 * ## Everything here hangs off ONE election
 *
 * The election is chosen in the shell's sub-bar, not per tab, and every screen below reads
 * it from the same store. PollSphere's tallying module has no election at all: its twenty-two
 * tabs operate on "the" election, which works exactly until there are two. A by-election
 * running beside a general one is the ordinary case, not the edge.
 *
 * ## Why the ballot comes first
 *
 * The tab order is the order the work happens in. **There is nothing to tally until a ballot
 * exists** — votes are cast for a candidate standing in a specific race, and a return keyed
 * to a candidacy that was never nominated is a number attached to nobody. So "Ballot &
 * Nominations" leads, and the counting tabs follow it.
 *
 * ## Tabs that are not built yet say so
 *
 * They are listed with `pending`, rendered dimmed and unclickable, rather than left out.
 * A module that reveals its shape one release at a time gives a desk no way to plan; a
 * greyed tab that names what is coming does.
 *
 * **`pending` must come off the moment a tab has a destination.** Import shipped dimmed for a
 * release after its wizard was finished, so the only signal a working feature existed was a
 * greyed-out label saying it did not. When adding a route under this shell, clear the flag in
 * the same change — a tab and its route are one thing, not two.
 *
 * Nothing is dimmed any more: every tab below has a route and a backend. `pending` and its
 * rendering stay, because the next module extension will need them and deleting the mechanism
 * would mean the next unbuilt tab is simply absent — which tells a desk nothing.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
  /** Not built yet — shown, dimmed, and not a link. */
  pending?: boolean
}

interface Group {
  label: string
  tabs: Tab[]
}

export const TALLY_GROUPS: Group[] = [
  {
    label: 'Ballot',
    tabs: [
      {
        to: '/tallying',
        label: 'Ballot & Nominations',
        hint: 'Who is standing, for what seat, on whose ticket — and their nomination status',
        permission: 'politics:candidacy-read',
        end: true,
      },
      {
        to: '/tallying/import',
        label: 'Import',
        hint: 'Load a nomination list from a spreadsheet or a scanned form',
        permission: 'politics:candidacy-import',
      },
    ],
  },
  {
    label: 'Count',
    tabs: [
      {
        to: '/tallying/board',
        label: 'Live board',
        hint: 'Results as they arrive, by seat',
        permission: 'tally:analyse',
      },
      {
        to: '/tallying/seats',
        label: 'Seat dashboards',
        hint: 'One race at a time, candidate by candidate',
        permission: 'tally:analyse',
      },
      {
        to: '/tallying/forms',
        label: 'Forms',
        hint: 'Upload a results form or key one in',
        permission: 'tally:read',
      },
      {
        to: '/tallying/returns',
        label: 'Returns',
        hint: 'Every set of figures filed, and what is waiting to be verified',
        permission: 'tally:read',
      },
      {
        to: '/tallying/analytics',
        label: 'Analytics',
        hint: 'Is the count healthy — arrivals, latency, reject rates, corrections',
        permission: 'tally:analyse',
      },
    ],
  },
  {
    label: 'Assure',
    tabs: [
      {
        to: '/tallying/variance',
        label: 'Variance',
        hint: 'Where the machine and the agent disagreed',
        permission: 'tally:read',
      },
      {
        to: '/tallying/reconciliation',
        label: 'Reconciliation',
        hint: 'Returns against registered voters and rejected ballots',
        permission: 'tally:analyse',
      },
      {
        to: '/tallying/disputes',
        label: 'Disputes',
        hint: 'Returns somebody has challenged',
        permission: 'tally:read',
      },
      {
        to: '/tallying/certificates',
        label: 'Certificates',
        hint: 'Declared results and their paperwork',
        permission: 'tally:read',
      },
    ],
  },
]

export function TallyLayout() {
  const { search } = useLocation()
  const authorities = useSession((s) => s.authorities)
  const { election } = useElectionContext()

  const groups = TALLY_GROUPS.map((g) => ({
    ...g,
    tabs: g.tabs.filter((t) => hasPermission(authorities, t.permission)),
  })).filter((g) => g.tabs.length > 0)

  return (
    <div className="space-y-4">
      <nav aria-label="Vote Tallying Centre" className="axis-table-scroll border-b border-border">
        <ul className="flex items-stretch gap-4 min-w-max">
          {groups.map((group) => (
            <li key={group.label} className="flex items-stretch gap-1">
              <span className="self-center pr-1 text-erp-xs uppercase tracking-wide text-muted-foreground/70"
                aria-hidden>
                {group.label}
              </span>
              {group.tabs.map((tab) => (tab.pending ? (
                <span key={tab.to} title={`${tab.hint} — not built yet`}
                  className="inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium
                             border-b-2 border-transparent -mb-px text-muted-foreground/45 cursor-default">
                  {tab.label}
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide">soon</span>
                </span>
              ) : (
                <NavLink key={tab.to} to={{ pathname: tab.to, search }} end={tab.end}
                  title={tab.hint}
                  className={({ isActive }) => cn(
                    'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors',
                    isActive
                      ? 'border-chocolate text-chocolate'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}>
                  {tab.label}
                </NavLink>
              )))}
            </li>
          ))}
        </ul>
      </nav>

      {/*
         The election, restated inside the module.

         It is already in the sub-bar, and repeating it is deliberate: these screens carry
         vote totals, and somebody reading a presidential figure needs the cycle it belongs
         to in their eye-line, not in the chrome two rows up.
      */}
      {election && (
        <p className="text-erp-xs text-muted-foreground -mt-2">
          Counting <strong className="text-foreground">{election.name}</strong> ·
          polling {new Date(`${election.pollingDate}T00:00:00`).toLocaleDateString()}
        </p>
      )}

      <Outlet />
    </div>
  )
}
